import React, { useState, useRef, useEffect } from 'react';
import './WheelEditor.css';

const PencilIcon = ({ active }) => (
  <svg
    width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke={active ? "#09F984" : "#38bdf8"}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const rgbToHex = (rgb) => {
  const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return null;
  return "#" + [m[1], m[2], m[3]]
    .map((n) => (+n).toString(16).padStart(2, "0"))
    .join("");
};

const isTransparent = (color) => {
  if (!color || color === "transparent") return true;
  const m = color.match(/rgba\([^)]+,\s*([\d.]+)\)/);
  if (m && parseFloat(m[1]) === 0) return true;
  return false;
};

const pickColorFromElement = (el) => {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    const bg = getComputedStyle(cur).backgroundColor;
    if (!isTransparent(bg)) return rgbToHex(bg);
    cur = cur.parentElement;
  }
  return null;
};


const toHex6 = (value) => {
  if (!value) return '#000000';
  if (value.startsWith('#') && value.length >= 7) return value.slice(0, 7);
  return '#000000';
};

const ColorRow = ({ label, colorKey, value, isPickActive, onPencilClick, onColorChange }) => (
  <div className="color-input">
    <label>{label}</label>
    <button
      className={`color-edit-btn${isPickActive ? " active" : ""}`}
      onClick={() => onPencilClick(colorKey)}
      title={isPickActive ? "İptal (Esc)" : "Sayfadan renk seç"}
    >
      <PencilIcon active={isPickActive} />
    </button>
    <label className="color-swatch-container" style={{ cursor: 'pointer' }}>
      <div className="color-swatch" style={{ backgroundColor: value }} />
      <input
        type="color"
        value={toHex6(value)}
        onChange={(e) => onColorChange(colorKey, e.target.value)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
      />
    </label>
    <span className="color-value-text">{value}</span>
  </div>
);

const WheelEditor = ({ colors, segments = [], onColorChange, onClose }) => {
  const [pickMode, setPickMode] = useState({ active: false, key: null });
  const editorRef = useRef(null);
  const onColorChangeRef = useRef(onColorChange);
  useEffect(() => { onColorChangeRef.current = onColorChange; }, [onColorChange]);

  const longLogoVisible = colors.longLogoVisible !== false;

  const cancelPick = () => setPickMode({ active: false, key: null });

  // Normal renk alanları için
  const handlePencilClick = async (key) => {
    if (pickMode.active && pickMode.key === key) { cancelPick(); return; }

    if (typeof window.EyeDropper !== "undefined") {
      try {
        const { sRGBHex } = await new window.EyeDropper().open();
        onColorChangeRef.current(key, sRGBHex);
      } catch {}
      return;
    }
    setPickMode({ active: true, key });
  };


  useEffect(() => {
    if (!pickMode.active) return;

    document.body.classList.add("color-pick-mode");

    const pick = (e) => {
      if (editorRef.current?.contains(e.target)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const hex = pickColorFromElement(e.target);
      if (hex) onColorChangeRef.current(pickMode.key, hex);
      cancelPick();
    };

    const cancel = (e) => { if (e.key === "Escape") cancelPick(); };

    document.addEventListener("click", pick, true);
    document.addEventListener("keydown", cancel);

    return () => {
      document.body.classList.remove("color-pick-mode");
      document.removeEventListener("click", pick, true);
      document.removeEventListener("keydown", cancel);
    };
  }, [pickMode.active, pickMode.key]);

  const row = (label, key) => (
    <ColorRow
      key={key}
      label={label}
      colorKey={key}
      value={colors[key] || "#000000"}
      isPickActive={pickMode.active && pickMode.key === key}
      onPencilClick={handlePencilClick}
      onColorChange={onColorChange}
    />
  );

  return (
    <div ref={editorRef} className="css-editor wheel-editor-panel">
      <div className="editor-header">
        <h3>Çark Editörü</h3>
        <button className="close-editor" onClick={onClose}>×</button>
      </div>

      {pickMode.active && (
        <div className="pick-hint">
          <span>Sayfada bir renge tıkla</span>
          <kbd>Esc</kbd>
          <span>iptal</span>
        </div>
      )}

      {/* Yeni Çark Tipi Bölümü */}
      <div className="editor-section">
        <h4>Çark Tasarım Şablonu</h4>
        <div className="color-input" style={{ display: 'block', marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '6px' }}>Çark Şablon Tipi</label>
          <select
            value={colors.wheelType || "cosmic"}
            onChange={(e) => onColorChange("wheelType", e.target.value)}
            style={{
              background: "#1e293b",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.2)",
              padding: '8px',
              borderRadius: '6px',
              width: '100%',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="cosmic">Neon Kozmik (Varsayılan)</option>
            <option value="diamond">Lüks Elmas</option>
            <option value="classic">Klasik Ampullü</option>
          </select>
        </div>
        
        <div className="color-input" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
          <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>Dilim Üzeri 3D Parlama Yansıması</label>
          <input
            type="checkbox"
            checked={colors.wheelOverlayShine !== false}
            onChange={(e) => onColorChange("wheelOverlayShine", e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
        </div>
      </div>

      {/* Tema Detay Renkleri (Sadece Kozmik ve Elmas için geçerli) */}
      {(colors.wheelType === "cosmic" || colors.wheelType === "diamond") && (
        <div className="editor-section">
          <h4>Şablon Detay Renkleri</h4>
          {row("Çerçeve Birincil Renk:", "wheelFrameColor1")}
          {row("Çerçeve İkincil Renk:", "wheelFrameColor2")}
          {colors.wheelType === "diamond" && row("Merkez Elmas Rengi:", "centerButtonCrystalColor")}
          {row("Merkez Yazı Rengi:", "centerButtonTextColor")}
        </div>
      )}

      {/* Manuel Dilim Emojileri/İkonları */}
      <div className="editor-section">
        <h4>Dilim İkonları</h4>
        <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '4px' }}>
          {segments.map((name, index) => {
            const currentIcon = (colors.segmentIcons && (colors.segmentIcons[index] || colors.segmentIcons[String(index)])) || "";
            const handleIconChange = (val) => {
              const updatedIcons = { ...(colors.segmentIcons || {}) };
              if (val) {
                updatedIcons[index] = val;
              } else {
                delete updatedIcons[index];
                delete updatedIcons[String(index)];
              }
              onColorChange("segmentIcons", updatedIcons);
            };

            return (
              <div key={index} className="slice-icon-row" style={{ marginBottom: '12px', background: 'rgba(255,255,255,0.03)', padding: '8px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Dilim {index + 1}: <strong>{name}</strong>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="İkon"
                    value={currentIcon}
                    onChange={(e) => handleIconChange(e.target.value)}
                    style={{
                      width: '50px',
                      background: '#1e293b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      padding: '4px 6px',
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontSize: '15px'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: 1 }}>
                    {["🛥️", "💻", "📱", "❌", "🪙", "⌚", "🎫", "🎟️", "💰", "🎁"].map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleIconChange(emoji)}
                        style={{
                          background: currentIcon === emoji ? 'rgba(0, 240, 255, 0.2)' : 'rgba(255,255,255,0.08)',
                          border: currentIcon === emoji ? '1px solid #00f0ff' : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '4px',
                          padding: '2px 4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="İkonu Seç"
                      >
                        {emoji}
                      </button>
                    ))}
                    {currentIcon && (
                      <button
                        onClick={() => handleIconChange("")}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          color: '#ef4444',
                          borderRadius: '4px',
                          padding: '2px 4px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                      >
                        Sil
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="editor-section">
        <h4>Çark Renkleri</h4>
        {row("Koyu Arka Plan:", "backgroundColor1")}
        {row("Açık Arka Plan:", "backgroundColor2")}
      </div>

      <div className="editor-section">
        <h4>Yazı Renkleri & Boyutları</h4>
        {row("Ana Yazı:", "textColor1")}
        {row("İkincil Yazı:", "textColor2")}
        <div className="color-input">
          <label>Dilim Yazı Boyutu: {colors.wheelSlicesFontSize || 20}px</label>
          <div className="slider-container">
            <input type="range" min="10" max="40" step="1"
              value={colors.wheelSlicesFontSize || 20}
              onChange={(e) => onColorChange('wheelSlicesFontSize', parseInt(e.target.value, 10))}
              className="opacity-slider"
            />
            <span className="color-value">{colors.wheelSlicesFontSize || 20}px</span>
          </div>
        </div>
      </div>

      <div className="editor-section">
        <h4>Diğer Renkler</h4>
        {row("Kenar Rengi:", "borderColor")}
        {row("İşaretçi Rengi:", "pointerColor")}
        {row("Çizgi Rengi:", "radiusLineColor")}
      </div>

      <div className="editor-section">
        <h4>Parıltı Efekti</h4>
        {row("Parıltı Rengi:", "wheelGlowColor")}
        <div className="color-input">
          <label>Parıltı Opaklığı: {(colors.wheelGlowOpacity * 100).toFixed(0)}%</label>
          <div className="slider-container">
            <input type="range" min="0" max="100"
              value={colors.wheelGlowOpacity * 100}
              onChange={(e) => onColorChange('wheelGlowOpacity', parseFloat((e.target.value / 100).toFixed(2)))}
              className="opacity-slider"
            />
            <span className="color-value">{(colors.wheelGlowOpacity * 100).toFixed(0)}%</span>
          </div>
        </div>
      </div>

      <div className="editor-section">
        <h4>Dekorasyon Renkleri</h4>
        {row("Halka / Ampul:", "ringAccentColor")}
        {row("Yonca Rengi:", "cloverColor")}
        {row("Işıma Rengi:", "ambientGlowColor")}
        {row("Sehpa Vurgu:", "standAccentColor")}
      </div>

      <div className="editor-section">
        <h4>Long Logo (Footer)</h4>
        <div
          className="long-logo-toggle-row"
          onClick={() => onColorChange('longLogoVisible', !longLogoVisible)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            padding: '6px 0',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '20px',
              borderRadius: '999px',
              background: longLogoVisible ? '#22c55e' : 'rgba(255,255,255,0.15)',
              position: 'relative',
              transition: 'background 0.2s ease',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '2px',
                left: longLogoVisible ? '18px' : '2px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }}
            />
          </div>
          <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
            {longLogoVisible ? 'Long Logo Görünür' : 'Long Logo Gizli'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default WheelEditor;
